import { useEffect, useState } from 'react';
import { Play, Loader2, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import ReconSection from './ReconSection';
import FileUploadField from './FileUploadField';
import HelpTip from './HelpTip';
import FilterToolbar, { FilterForm } from './FilterToolbar';

/**
 * Generic Oracle <-> ADP field reconciliation UI, driven by an OGPI category
 * (employment, bank, email). Uses /api/field-recon/*.
 */
export default function FieldRecon({ category, title, description, oracleHint, adpHint }) {
  const [oracleFile, setOracleFile] = useState(null);
  const [adpFile, setAdpFile] = useState(null);
  const [mappingInfo, setMappingInfo] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filterDraft, setFilterDraft] = useState('');
  const [filter, setFilter] = useState('');
  const [issueCategory, setIssueCategory] = useState('');

  useEffect(() => {
    setResult(null);
    setError('');
    setFilter('');
    setFilterDraft('');
    setIssueCategory('');

    apiFetch(`/api/field-recon/mappings?category=${category}`)
      .then(setMappingInfo)
      .catch(() => setMappingInfo(null));

    apiFetch(`/api/field-recon/summary?category=${category}`)
      .then((d) => {
        if (d.summary) {
          return apiFetch(`/api/field-recon/exceptions?category=${category}&limit=5000`).then((ex) =>
            setResult({ summary: d.summary, exceptions: ex.rows || [] })
          );
        }
      })
      .catch(() => {});
  }, [category]);

  const runRecon = async () => {
    setError('');
    setResult(null);
    setRunning(true);
    try {
      if (!oracleFile || !adpFile) {
        throw new Error('Upload both the Oracle and ADP reports.');
      }
      const fd = new FormData();
      fd.append('category', category);
      fd.append('oracleReport', oracleFile);
      fd.append('adpReport', adpFile);
      const base = getApiBase();
      const res = await fetch(`${base}/api/field-recon/run`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reconciliation failed');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const summary = result?.summary;
  const rows = result?.exceptions || [];
  const needle = filter.trim().toLowerCase();
  const issueCategories = summary?.exceptionsByCategory
    ? Object.keys(summary.exceptionsByCategory)
    : [];

  const visible = rows.filter((r) => {
    if (issueCategory && r.category !== issueCategory) return false;
    if (!needle) return true;
    return [r.personNumber, r.gvFieldName, r.oracleSourceElement, r.description, r.oracleValue, r.adpValue, r.detail, r.category]
      .map((v) => String(v ?? '').toLowerCase())
      .some((v) => v.includes(needle));
  });

  const runLabel = summary?.runAt
    ? new Date(summary.runAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Pre-payroll validation</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">{title}</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">{description}</p>
        {mappingInfo && (
          <p className="mt-2 text-xs text-slate-500">
            {mappingInfo.fieldCount} mapped fields · infotypes {mappingInfo.infotypes?.join(', ')}
            {mappingInfo.valueSets?.length > 0 && (
              <> · coded values aligned ({mappingInfo.valueSets.join(', ')})</>
            )}
          </p>
        )}
      </header>

      <ReconSection
        title="Sources & run"
        description="Oracle report and ADP report. Columns are matched by header names."
        headerCenter={
          <button type="button" onClick={runRecon} disabled={running} className="btn-primary">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run
          </button>
        }
        headerExtra={runLabel && <p className="text-right text-xs text-slate-500">Last run {runLabel}</p>}
      >
        <div className="grid gap-3">
          <FileUploadField label="Oracle report" hint={oracleHint} accept=".xlsx,.xls" file={oracleFile} onChange={setOracleFile} helpTerm="globalId" HelpTipComponent={HelpTip} />
          <FileUploadField label="ADP report" hint={adpHint} accept=".xlsx,.xls" file={adpFile} onChange={setAdpFile} helpTerm="globalId" HelpTipComponent={HelpTip} />
        </div>
        {error && <div className="alert-error mt-4">{error}</div>}
      </ReconSection>

      {summary && (
        <>
          <div className="flex flex-wrap gap-2">
            <span className="badge-neutral inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              Oracle {summary.personPresence?.oracleCount?.toLocaleString() ?? '—'} · ADP{' '}
              {summary.personPresence?.adpCount?.toLocaleString() ?? '—'} · both{' '}
              {summary.personPresence?.both?.toLocaleString() ?? '—'}
            </span>
            <span className="badge-success inline-flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {summary.matchedCount?.toLocaleString() ?? 0} matched
            </span>
            <span className="badge-warning inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              {summary.exceptionCount?.toLocaleString() ?? 0} exceptions
            </span>
          </div>

          {issueCategories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {issueCategories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setIssueCategory(issueCategory === c ? '' : c)}
                  className={issueCategory === c ? 'badge-warning' : 'badge-neutral'}
                  title="Filter by issue type"
                >
                  {c}: {summary.exceptionsByCategory[c].toLocaleString()}
                </button>
              ))}
            </div>
          )}

          <div className="card-panel overflow-hidden">
            <div className="section-header flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-100">Exceptions</h3>
              <FilterForm onApply={() => setFilter(filterDraft)}>
                <FilterToolbar
                  onClear={() => {
                    setFilterDraft('');
                    setFilter('');
                  }}
                  hasActiveFilters={!!filter.trim()}
                >
                  <input
                    type="search"
                    placeholder="Person, field, value…"
                    value={filterDraft}
                    onChange={(e) => setFilterDraft(e.target.value)}
                    className="filter-input min-w-[12rem] text-sm"
                  />
                </FilterToolbar>
              </FilterForm>
            </div>
            <div className="table-scroll">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Issue</th>
                    <th>GV field</th>
                    <th>Oracle</th>
                    <th>ADP</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-slate-500">
                        No exceptions match the filter.
                      </td>
                    </tr>
                  ) : (
                    visible.slice(0, 500).map((r, i) => (
                      <tr key={`${r.personNumber}-${r.gvFieldName}-${i}`}>
                        <td className="font-mono">{r.personNumber}</td>
                        <td>{r.category}</td>
                        <td className="font-mono">{r.gvFieldName || '—'}</td>
                        <td className="max-w-[10rem] truncate" title={r.oracleValue}>{r.oracleValue || '—'}</td>
                        <td className="max-w-[10rem] truncate" title={r.adpValue}>{r.adpValue || '—'}</td>
                        <td className="max-w-xs truncate text-slate-400" title={r.detail}>{r.detail || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {visible.length > 500 && (
              <p className="px-4 py-2 text-xs text-slate-500">
                Showing first 500 of {visible.length.toLocaleString()} rows.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
